import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const A = "https://customer-assets-cm19k8pv.emergentagent.net/job_strategy-led-2/artifacts";

const CASES = [
  {
    client: "Queen Square Imaging Centre",
    sector: "Healthcare",
    image: `${A}/9j04cx5c_IMG_0625.webp`,
    gallery: [
      `${A}/v4yun7n2_Your%20hand%20shakes%20when%20it%E2%80%99s%20still..png`,
      `${A}/2wwlqzxu_Resolution.png`,
      `${A}/tr6m1tdq_Queen%20Square%20Social%20Enterprise%20Insta%20%281%29%20copy.png`,
    ],
    challenge: "A trusted clinic with an inconsistent social presence and no clear content direction.",
    strategy: "Defined clear content pillars around trust, education and patient experience, then built a structured monthly content plan.",
    delivery: "Delivered ongoing social media management, successful paid awareness campaigns, asset management and clear monthly reporting.",
    results: ["Increased awareness", "Higher engagement", "More website traffic"],
    services: ["Social Media Management", "Content Strategy"],
    quote: "Reporting is always clear and timely, and they are responsive, professional and easy to work with.",
    quoteBy: "Peter Sutton, Operations Manager",
  },
  {
    client: "DBM Talent Management",
    sector: "Talent agency",
    fit: "contain",
    image: `${A}/f6pdfjr3_Screenshot%202026-07-16%20at%2018.00.18.webp`,
    gallery: [
      `${A}/i25psgep_Screenshot%202026-07-16%20at%2018.01.14.webp`,
      `${A}/ubnocp1i_Screenshot%202026-07-16%20at%2018.01.39.webp`,
    ],
    challenge: "A boutique London agency representing actors and writers, with a brand that deserved a more premium, considered online presence.",
    strategy: "Shaped confident, clear messaging and a structure that reflects the agency's personal, hands-on approach to representation.",
    delivery: "Designed and built a premium website with a refined navy and gold visual identity, alongside clearer journeys for talent, industry professionals and new enquiries.",
    results: ["Premium first impression", "Clearer positioning", "Stronger brand presence"],
    services: ["Website Design"],
    quote: null,
  },
];

export default function Work() {
  useEffect(() => {
    document.title = "Selected Work | Track Digital";
  }, []);

  return (
    <main data-testid="work-page">
      <section className="pt-36 md:pt-44 pb-16 md:pb-20">
        <div className="td-container">
          <Reveal>
            <span className="td-label block mb-6">Selected Work</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] max-w-4xl">
              How structured thinking becomes useful digital work.
            </h1>
            <p className="td-measure text-lg md:text-xl text-[#4a4a4a] leading-relaxed mt-8">
              A look at how we help businesses bring clarity, structure and consistency to their
              marketing, from managed social media to premium website design.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-10">
        <div className="td-container flex flex-col gap-20 md:gap-28">
          {CASES.map((c, i) => (
            <Reveal key={c.client}>
              <article
                data-testid={`case-study-${i}`}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-start ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
              >
                <div className="lg:col-span-6 [direction:ltr]">
                  <div className={`aspect-[16/11] rounded-3xl overflow-hidden border border-[#eee] bg-[#fafafa] ${c.fit === "contain" ? "" : "img-zoom"}`}>
                    <img src={c.image} alt={c.client} className={`w-full h-full ${c.fit === "contain" ? "object-contain" : "object-cover"}`} loading="lazy" />
                  </div>
                  {c.gallery && (
                    <div className={`mt-4 grid gap-4 ${c.gallery.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
                      {c.gallery.map((g, gi) => (
                        <div key={gi} className={`rounded-2xl overflow-hidden border border-[#eee] bg-[#fafafa] ${c.fit === "contain" ? "aspect-[16/10] flex items-center" : "aspect-square"}`}>
                          <img src={g} alt={`${c.client} sample ${gi + 1}`} className={`w-full h-full ${c.fit === "contain" ? "object-contain" : "object-cover"}`} loading="lazy" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="lg:col-span-6 [direction:ltr]">
                  <span className="td-label block mb-4">{c.sector}</span>
                  <h2 className="text-2xl md:text-3xl font-semibold mb-6">{c.client}</h2>
                  <div className="space-y-4 mb-6">
                    <div>
                      <p className="text-sm font-semibold text-[#7C6CF0] mb-1">Challenge</p>
                      <p className="text-[#4a4a4a]">{c.challenge}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#7C6CF0] mb-1">Strategy</p>
                      <p className="text-[#4a4a4a]">{c.strategy}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#7C6CF0] mb-1">Delivery</p>
                      <p className="text-[#4a4a4a]">{c.delivery}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {c.results.map((r) => (
                      <span key={r} className="rounded-full bg-[#fafafa] border border-[#e5e5e5] px-4 py-1.5 text-sm text-[#2b2b2b]">
                        {r}
                      </span>
                    ))}
                  </div>
                  {c.quote && (
                    <blockquote className="border-l-2 border-[#7C6CF0] pl-4 text-[#2b2b2b] italic mb-3">
                      {c.quote}
                    </blockquote>
                  )}
                  {c.quoteBy && <p className="text-sm text-[#8a8a8a] mb-6">{c.quoteBy}</p>}
                  <div className="flex flex-wrap gap-3">
                    {c.services.map((s) => (
                      <span key={s} className="text-xs uppercase tracking-wider text-[#8a8a8a]">{s}</span>
                    ))}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="td-container py-16">
        <Link to="/services" className="inline-flex items-center gap-2 text-[#1a1a1a] font-medium hover:text-[#7C6CF0] transition-colors">
          Explore our services <ArrowRight size={18} />
        </Link>
      </div>

      <CTASection
        label="Your Project Next"
        heading="Let's build something that moves your business forward."
        copy="Book a discovery call and we'll explore how structured thinking can help your business grow."
      />
    </main>
  );
}
